// src/pages/EmailPasswordLogin/pinCode.js
import React, {useRef} from 'react';

/* ---- helpers defined BEFORE use ---- */
const pad = (v, len) => {
	const s = (v || '').slice(0, len);
	return s + ' '.repeat(len - s.length);
};
const sanitize = (s) => (s || '').replace(/[^a-zA-Z0-9]/g, ''); // keep case
const setCharAt = (str, idx, ch, len) => {
	const a = pad(str, len).split('');
	a[idx] = ch;
	return a.join('');
};

export default function PinCode({
	length = 6,
	value = '',
	onChange,
	onComplete,
	name = 'pin',
	status, // 'error' | 'success' | undefined
	shakeKey, // change this value to trigger shake animation
}) {
	const chars = (value || '').split('');

	// array of refs for each box
	const inputsRef = useRef([]);
	if (inputsRef.current.length !== length) {
		inputsRef.current = Array.from(
			{length},
			(_, i) => inputsRef.current[i] || React.createRef()
		);
	}

	const focusBox = (idx) => {
		const el = inputsRef.current[idx]?.current;
		if (el) el.focus();
	};

	const handleInput = (idx) => (e) => {
		const raw = e.target.value;
		if (!raw) {
			const next = setCharAt(value, idx, '', length).replace(/\s/g, '');
			onChange(next);
			return;
		}
		const s = sanitize(raw);
		if (!s) {
			e.target.value = '';
			return;
		}

		let next = value;
		let cursor = idx;
		for (let i = 0; i < s.length && cursor < length; i++) {
			next = setCharAt(next, cursor, s[i], length);
			cursor++;
		}
		next = next.replace(/\s/g, '');
		onChange(next);

		// move focus after last inserted
		const nextIndex = Math.min(idx + s.length, length - 1);
		focusBox(nextIndex);

		if (s.length === 1 && idx < length - 1) focusBox(idx + 1);
		if (next.length === length) onComplete(next);
	};

	const handleKeyDown = (idx) => (e) => {
		const key = e.key;

		if (key === 'ArrowLeft' && idx > 0) {
			e.preventDefault();
			focusBox(idx - 1);
		}
		if (key === 'ArrowRight' && idx < length - 1) {
			e.preventDefault();
			focusBox(idx + 1);
		}

		if (key === 'Backspace') {
			e.preventDefault();
			const currentVal = chars[idx] || '';
			let next = value;
			if (currentVal) {
				next = setCharAt(next, idx, '', length);
				onChange(next.replace(/\s/g, ''));
			} else if (idx > 0) {
				focusBox(idx - 1);
				next = setCharAt(next, idx - 1, '', length);
				onChange(next.replace(/\s/g, ''));
			}
		}
	};

	const handlePaste = (idx) => (e) => {
		const data = e.clipboardData.getData('text');
		if (!data) return;
		e.preventDefault();
		const s = sanitize(data);
		if (!s) return;

		let next = value;
		let cursor = idx;
		for (let i = 0; i < s.length && cursor < length; i++) {
			next = setCharAt(next, cursor, s[i], length);
			cursor++;
		}
		next = next.replace(/\s/g, '');
		onChange(next);
		focusBox(Math.min(idx + s.length, length - 1));
		if (next.length === length) onComplete(next);
	};

	return (
		<div
			className={[
				'pin-grid',
				status === 'error' && 'has-error',
				status === 'success' && 'has-success',
				shakeKey && 'is-shaking', // toggled by parent when you want a shake
			]
				.filter(Boolean)
				.join(' ')}
			style={{['--pin-len']: length}}
			role="group"
			aria-label="PIN Eingabe"
		>
			{Array.from({length}).map((_, i) => {
				const filled = !!chars[i];
				return (
					<input
						key={i}
						ref={inputsRef.current[i]}
						className={['pin-box', filled && 'is-filled']
							.filter(Boolean)
							.join(' ')}
						type="text"
						inputMode="text"
						autoCapitalize="none"
						autoComplete={i === 0 ? 'one-time-code' : 'off'}
						pattern="[A-Za-z0-9]"
						aria-label={`PIN Zeichen ${i + 1}`}
						maxLength={1}
						value={chars[i] || ''}
						onChange={(e) => {
							const input = e.target; // real DOM element reference
							const value = e.target.value;
							const before = !!chars[i];
							handleInput(i)(e);
							const after = (e.target.value || '').length > 0;
							// add just-filled class for tiny pop
							if (!before && after) {
								input.classList.add('just-filled');
								setTimeout(
									() => input.classList.remove('just-filled'),
									50
								);
							}
						}}
						onKeyDown={handleKeyDown(i)}
						onPaste={handlePaste(i)}
					/>
				);
			})}
			{name && <input type="hidden" name={name} value={value} />}
		</div>
	);
}
